import React, { useState, useCallback, useMemo, useEffect } from 'react';
import {
  Card,
  Button,
  Space,
  Typography,
  Row,
  Col,
  message,
} from 'antd';
import {
  PlusOutlined,
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { getPricePlans, addPricePlan, updatePricePlan } from '../../api/pricePlans';
import type { PricePlan } from '../../types/pricePlans';
import PricePlansTable from '../../components/features/pricePlans/PricePlansTable';
import PricePlanModal from '../../components/features/pricePlans/PricePlanModal';

const { Title } = Typography;
const PAGE_SIZE = 10;

type LastDocs = { [page: number]: any };

type PricePlanModalForm = Omit<PricePlan, 'id'>;

const PricePlans: React.FC = () => {
  const { t } = useTranslation(['finance', 'common']);
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const [editingPlan, setEditingPlan] = useState<PricePlan | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [page, setPage] = useState<number>(1);
  const [lastDocs, setLastDocs] = useState<LastDocs>({});
  const [hasNext, setHasNext] = useState<boolean>(false);

  const { data = { pricePlans: [], lastDoc: undefined }, isLoading } = useQuery<{ pricePlans: PricePlan[]; lastDoc?: any }>({
    queryKey: ['pricePlans', page, lastDocs[page - 1]],
    queryFn: async ({ queryKey }) => {
      const [_key, _page, lastDoc] = queryKey as [string, number, any];
      return getPricePlans(PAGE_SIZE, lastDoc);
    },
    staleTime: 5 * 60 * 1000,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    retry: false,
    placeholderData: (previousData) => previousData,
  });

  useEffect(() => {
    if (data.lastDoc) {
      setLastDocs(prev => ({ ...prev, [page]: data.lastDoc }));
      setHasNext(!!data.lastDoc && data.pricePlans.length === PAGE_SIZE);
    }
  }, [data, page]);

  const pricePlans: PricePlan[] = data.pricePlans;

  const addMutation = useMutation({
    mutationFn: (values: PricePlanModalForm) => addPricePlan(values),
    onSuccess: () => {
      message.success('Price plan added');
      queryClient.invalidateQueries({ queryKey: ['pricePlans'] });
      setIsModalOpen(false);
      setEditingPlan(null);
    },
    onError: () => message.error('Failed to add price plan'),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, ...data }: { id: string } & Partial<PricePlanModalForm>) => updatePricePlan(id, data),
    onSuccess: () => {
      message.success('Price plan updated');
      queryClient.invalidateQueries({ queryKey: ['pricePlans'] });
      setIsModalOpen(false);
      setEditingPlan(null);
    },
    onError: () => message.error('Failed to update price plan'),
  });

  const handleAdd = useCallback(() => {
    setEditingPlan(null);
    setIsModalOpen(true);
  }, []);

  const handleEdit = useCallback((plan: PricePlan) => {
    setEditingPlan(plan);
    setIsModalOpen(true);
  }, []);

  const handleView = useCallback((plan: PricePlan) => {
    navigate(`/finance/price-plans/${plan.id}`, { state: { pricePlan: plan } });
  }, [navigate]);

  const handleModalSuccess = useCallback((values: PricePlanModalForm) => {
    if (editingPlan) {
      updateMutation.mutate({ id: editingPlan.id, ...values });
    } else {
      addMutation.mutate(values);
    }
  }, [editingPlan, addMutation, updateMutation]);

  const handleModalCancel = useCallback(() => {
    setIsModalOpen(false);
    setEditingPlan(null);
  }, []);

  const pagination = useMemo(() => ({
    current: page,
    pageSize: PAGE_SIZE,
    total: hasNext ? (page + 1) * PAGE_SIZE : page * PAGE_SIZE,
    onChange: (newPage: number) => setPage(newPage),
  }), [page, hasNext]);

  return (
    <div>
      <Card>
        <Space direction="vertical" style={{ width: '100%' }} size="large">
          <Row justify="space-between" align="middle">
            <Col>
              <Title level={4}>{t('pricePlans.title')}</Title>
            </Col>
            <Col>
              <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd}>
                {t('pricePlans.add')}
              </Button>
            </Col>
          </Row>
          <PricePlansTable
            pricePlans={pricePlans}
            loading={isLoading}
            pagination={pagination}
            onEdit={handleEdit}
            onView={handleView}
            onTableChange={pagination.onChange}
          />
        </Space>
      </Card>
      <PricePlanModal
        open={isModalOpen}
        pricePlan={editingPlan}
        onSuccess={() => handleModalSuccess as any}
        onCancel={handleModalCancel}
      />
    </div>
  );
};

export default PricePlans; 